import i18n from '@/i18n';
import Vue from 'vue';
import Cookies from 'js-cookie';
import store from '../';
import { DateTime } from 'luxon';
import { MutationTree } from 'vuex';
import { CartState } from '../types';
import { state as restaurantState } from '../restaurant/restaurant';
import { state as authState } from '../auth/auth';
import { state as suitesState } from '../suites/suites';
import { formatTemporarySelectedOrderItem, formatOrderOptionsToCompare } from '../../utils/format';
import { sortOrderOptions } from '@/utils/sort';
import { getElementBySkuOrId } from '@/utils/helpers';
import { updateCartCookie, updateRotationalMenuOrdersCookie, getItemsFromCookie, getRotationalMenuOrdersFromCookie, CART_COOKIE, ROTATIONAL_MENU_ORDERS_COOKIE } from '@/utils/restore-cart';
import { getEligibleSubsidies, compareSubsidyItem } from '@/utils/pricing';

/**
 * Set the frontend order price (this price is just for visual purposes
 * and will not be used for actual calculations, all that happens in the
 * backend)
 *
 * @param {OrderItem} item
 * @return {void}
 */
function setLocalOrderPrice(item: OrderItem): OrderItem {
	const { addonPrice, addonAdjustedPriceFound, addonAdjustedPrice } = setAddonsLocalPrice(item, 'orderOptions');

	// Set adjusted order price if the item has an adjusted price (member/subsidy), if there was an adjusted price in any of the options selected
	if(item.adjustedPrice || addonAdjustedPriceFound) {
		item.orderPrice = +(((item.adjustedPrice !== undefined ? +item.adjustedPrice : +item.price) + addonAdjustedPrice) * item.quantity).toFixed(2);
	}
	else {
		item.orderPrice = +((+item.price + addonPrice) * item.quantity).toFixed(2);
	}

	return item;
}

/**
 * Set the frontend order price (this price is just for visual purposes
 * and will not be used for actual calculations, all that happens in the
 * backend)
 *
 * @param {OrderItem | OrderOptionGroup} item
 * @param {string} arrayName
 * @return {void}
 */
function setAddonsLocalPrice(item: any, arrayName: string): AddonsLocalOrderPriceInfo {
	let addonPrice: number = 0;
	let addonAdjustedPriceFound: boolean = false;
	let addonAdjustedPrice: number = 0;

	if(item[arrayName] && item[arrayName].length) {
		item[arrayName].forEach((orderOption: OrderOptionGroup) => {
			orderOption.values!.forEach((value: OrderOption) => {
				const quantity = value.quantity || 0;
				// Don't add to addon price if it's a pricing option since it overrides the item's price instead
				if(orderOption.optionGroupType !== 'pricing') {
					// Add to total addon adjusted price if the addon has one
					// If there is none we add it to the total anyways, since
					// some options may have a normal price where the other has
					// an adjusted price (member / subsidy), we need the total.
					if(value.adjustedPrice !== undefined) {
						addonAdjustedPriceFound = true;
						addonAdjustedPrice += +value.adjustedPrice * quantity;
					}
					else {
						addonAdjustedPrice += +value.price! * quantity;
					}

					addonPrice += +value.price! * quantity;
				}

				// Get sub options price if any and add them to our current amounts
				const subOptionAddonPrices = setAddonsLocalPrice(value, 'options');
				addonPrice += subOptionAddonPrices.addonPrice * quantity;
				addonAdjustedPrice += subOptionAddonPrices.addonAdjustedPrice * quantity;
				if(!addonAdjustedPriceFound) {
					addonAdjustedPriceFound = subOptionAddonPrices.addonAdjustedPriceFound;
				}
			});
		});
	}

	return { addonPrice, addonAdjustedPriceFound, addonAdjustedPrice };
}

/**
 * Re-apply subsidy pricing to cart items after restoration from cookies
 *
 * @param {OrderItem[]} items
 * @return {void}
 */
export function reapplySubsidyPricingToCartItems(items: OrderItem[]): void {
	if (restaurantState.restaurant.subsidiesEnabled && restaurantState.restaurant.subsidies?.length) {
		const eligibleSubsidies = getEligibleSubsidies(restaurantState.restaurant.subsidies, authState);

		if (eligibleSubsidies.length) {
			items.forEach((item: OrderItem) => {
				const hasPricingOptions = item.orderOptions && item.orderOptions?.some((orderOption: OrderOptionGroup) => orderOption.optionGroupType === 'pricing') || false;
				compareSubsidyItem(item, eligibleSubsidies, 'orderOptions', hasPricingOptions);
			});

			// Recalculate order prices with subsidy pricing
			items.forEach((item: OrderItem, index: number) => {
				items[index] = { ...setLocalOrderPrice(item) };
			});
		}
	}
}

/**
 * Finds all the default options and sets them as selected for the temporary item
 *
 * @param {OptionGroup[]} optionGroups
 * @return {OptionGroup[]}
 */
function setDefaultOrderOptions(optionGroups?: OptionGroup[]): OptionGroup[] {
	if (!optionGroups) {
		return [];
	}
	const optionGroupsCopy = structuredClone(optionGroups);

	return optionGroupsCopy.reduce((acc: OptionGroup[], optionGroup: OptionGroup) => {
		if (optionGroup.type === 'pricing') {
			return acc;
		}
		const defaultOptions = optionGroup.values?.filter((option: Option) => option.default_selection);

		if (defaultOptions && defaultOptions.length) {
			// Recursively set default options for sub options
			defaultOptions.forEach((option: Option) => {
				if (option.options && option.options.length) {
					option.options = setDefaultOrderOptions(option.options);
				}
			});
			optionGroup.values = defaultOptions;
			acc.push(optionGroup);
		}
		return acc;
	}, []);
}

function setLastOrderOptions(tempItem: MenuItem): OptionGroup[] {
	if (!tempItem.options) {
		return [];
	}
	const optionGroupsCopy = structuredClone(tempItem.options);

	return optionGroupsCopy.reduce((acc: OptionGroup[], optionGroup: OptionGroup) => {
		const lastOrderedOptions: Option[] | undefined = optionGroup.values?.filter((option: Option) => tempItem.lastOrderedOptions?.map(({ id, sku }: LastOrderedItem) => ({ id, sku })).some(({ id, sku }) => getElementBySkuOrId(option, id, sku)));
		if (lastOrderedOptions?.length) {
			optionGroup.values = lastOrderedOptions.map((option: Option) => {
				const lastOrderedOption = tempItem.lastOrderedOptions?.find(({ id, sku }: LastOrderedItem) => getElementBySkuOrId(option, id, sku));
				// Update the quantity of the option with the quantity of the matching lastOrderedOption
				if (lastOrderedOption?.quantity) {
					option.quantity = lastOrderedOption.quantity;
				}
				return option;
			});

			acc.push(optionGroup);
		}
		return acc;
	}, []);
}

/**
 * Add special_instructions field to the object.
 *
 * @param {OrderItem} item
 * @param {any} extraData
 * @return {OrderItem}
 */
function addTempFields(item: OrderItem, extraData: any): OrderItem {
	if (extraData.special_instructions) {
		item.orderOptions!.push(
			{
				name: i18n.t('utils.vuex_store.cart.special_instructions_name'),
				memo: true,
				values: [
					{
						name: extraData.special_instructions,
						price: '0',
						quantity: 1,
						roll_up: true
					}
				]
			}
		);
	}
	return item;
}

/**
 * Check if the item has pricing options, then check if the item has a pricing option selected
 * If item.orderOptions doesn't have a pricing option group selected, we add the price_type (default price name) as the default
 * This is for visual purposes only, this option will be ignored by the Remy when validating the item and setting costs
 *
 * @param {OrderItem} item
 * @return {OrderItem}
 */
function addDefaultPricingOption(item: OrderItem): OrderItem {
	const hasPricingOptions = !!(item.options?.find((optionGroup: OptionGroup) => optionGroup.type === 'pricing' && optionGroup.values?.length));
	if (hasPricingOptions) {
		const pricingOptionSelected = !!(item.orderOptions?.find(option => option.optionGroupType === 'pricing'));

		if (!pricingOptionSelected) {
			item.orderOptions = [
				{
					name: 'pricing',
					optionGroupType: 'pricing',
					defaultPricingOption: true,
					values: [{
						name: item.price_type,
						price: item.price,
						quantity: 1,
						roll_up: true,
						localization: Object.keys(item.localization || {}).reduce((acc, locale) => {
							const priceType = item.localization?.[locale]?.price_type;
							if (priceType) {
								acc[locale] = { name: priceType };
							}
							return acc;
						}, {} as Record<string, { name: string }>)
					}]
				},
				...(item.orderOptions || [])
			];
		}
	}
	delete item.options; // Remove options from order item, orderOptions contatins the selected options
	return item;
}

/**
 * Update order fields: quantity, price and id
 *
 * @param {OrderItem} tempItem
 * @param {OrderItem} item
 * @return {OrderItem}
 */
function updateOrderFields(tempItem: OrderItem, item: OrderItem): OrderItem {
	tempItem.quantity += item.quantity;
	item.orderPrice = item.orderPrice! + tempItem.orderPrice!;
	tempItem.orderPrice = item.orderPrice;
	tempItem.orderId = item.orderId;
	// Set the prices with quantity update
	tempItem = { ...setLocalOrderPrice(tempItem) };
	return tempItem;
}

export const mutations: MutationTree<CartState> = {
	/**
	 * Add the temporary item to the OrderItem list.
	 * If the location is a K12 location, we also add the item to the rotationalMenuOrders list for the given date.
	 *
	 * @param {CartState} state
	 * @param {any} extraData
	 * @return {void}
	 */
	ADD_TO_CART(state: CartState, extraData: any): void {
		if(state.temporarySelectedItem) {
			state.temporarySelectedItem = addTempFields(state.temporarySelectedItem, extraData);
			state.temporarySelectedItem = addDefaultPricingOption(state.temporarySelectedItem);
			state.temporarySelectedItem.orderOptions! = sortOrderOptions(state.temporarySelectedItem.orderOptions!);
			const { itemsInCart } = extraData;
			const isK12Location = restaurantState.restaurant.k12;

			if(itemsInCart.length) {
				let existInArray = false;
				let orderOptionsSame = false;

				/**
				 * We loop through the items to check if the item already exists in the array,
				 * from there we check if the order options are the same, if they are we simply increase
				 * the quantity.
				 */
				for (let index = 0; index < itemsInCart.length; index++) {
					if(itemsInCart[index].id === state.temporarySelectedItem.id) {
						existInArray = true;
						const tempItemOrderOptions = formatOrderOptionsToCompare(state.temporarySelectedItem.orderOptions);
						const itemToCompareOrderOptions = formatOrderOptionsToCompare(itemsInCart[index].orderOptions);
						if(JSON.stringify(tempItemOrderOptions) === JSON.stringify(itemToCompareOrderOptions)) {
							orderOptionsSame = true;
							state.temporarySelectedItem = updateOrderFields(state.temporarySelectedItem, itemsInCart[index]);
							itemsInCart.splice(index, 1, state.temporarySelectedItem);
							updateCartCookie(itemsInCart, restaurantState.restaurant.id, suitesState.eventSuiteId);
							break;
						}
					}
				}

				/**
				 * If the item exists in the array, we check if the options were the same
				 * so we don't add the item twice
				 */
				if(existInArray) {
					if(!orderOptionsSame) {
						state.temporarySelectedItem.orderId = state.orderId++;
						itemsInCart.push(state.temporarySelectedItem);
						updateCartCookie(itemsInCart, restaurantState.restaurant.id, suitesState.eventSuiteId);
					}
				}

				/**
				 * If the item does not exists in the array, we simply add it
				 */
				else {
					state.temporarySelectedItem.orderId = state.orderId++;
					itemsInCart.push(state.temporarySelectedItem);
					updateCartCookie(itemsInCart, restaurantState.restaurant.id, suitesState.eventSuiteId);
				}

				// If the location is a K12 location and an order already exists in the cart for the given date, we update the order items
				if (isK12Location && extraData.orderDate) {
					state.rotationalMenuOrders.findIndex((order: RotationalMenuOrder) => {
						if(order.date === extraData.orderDate) {
							order.items = itemsInCart;
							updateRotationalMenuOrdersCookie(order, restaurantState.restaurant.id);
						}
					});

				}
			}

			/**
			 * Array empty so we simply add to it
			 */
			else {
				state.temporarySelectedItem.orderId = state.orderId++;
				itemsInCart.push(state.temporarySelectedItem);
				updateCartCookie(itemsInCart, restaurantState.restaurant.id, suitesState.eventSuiteId);

				// Add the item to the rotationalMenuOrders list if the location is a K12 location
				if (isK12Location && extraData.orderDate) {
					const updatedOrder = {
						items: itemsInCart,
						orderId: state.orderId,
						date: extraData.orderDate
					};
					state.rotationalMenuOrders.push(updatedOrder);
					updateRotationalMenuOrdersCookie(updatedOrder, restaurantState.restaurant.id);
				}
			}
		}
	},

	/**
	 * Update quantity of the temporary item.
	 * Either increment by one or set to a specific quantity.
	 *
	 * @param {CartState} state
	 * @param {number} [quantity]
	 * @return {void}
	 */
	UPDATE_TEMPORARY_ITEM_QUANTITY(state: CartState, quantity?: number): void {
		if (state.temporarySelectedItem) {
			state.temporarySelectedItem.quantity = quantity || state.temporarySelectedItem.quantity + 1;
			// Break object reference for reactivity purposes (Vue3 fixes this)
			state.temporarySelectedItem = { ...setLocalOrderPrice(state.temporarySelectedItem) };
		}
	},

	/**
	 * Decrease the quantity of the temporary item
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	REMOVE_TEMPORARY_ITEM(state: CartState): void {
		if (state.temporarySelectedItem) {
			if(state.temporarySelectedItem.quantity >= 2) {
				state.temporarySelectedItem.quantity--;
				state.temporarySelectedItem = { ...setLocalOrderPrice(state.temporarySelectedItem) };
			}
		}
	},

	/**
	 * Set temporary item when user is on the expanded view so we can
	 * manipulate the data if the item is added later on.
	 *
	 * @param {CartState} state
	 * @param {{ tempItem: MenuItem, quantity?: number }} tempItemPayload
	 * @return {void}
	 */
	SET_TEMPORARY_ITEM(state: CartState, { tempItem, quantity }: { tempItem: MenuItem, quantity?: number }): void {
		const itemCopy = formatTemporarySelectedOrderItem(tempItem);
		itemCopy.quantity = quantity || 1;
		itemCopy.orderOptions = tempItem.lastOrderedOptions?.length ? setLastOrderOptions(tempItem) : setDefaultOrderOptions(tempItem.options);
		state.temporarySelectedItem = itemCopy as OrderItem;
		state.temporarySelectedItem = { ...setLocalOrderPrice(state.temporarySelectedItem) };
	},

	/**
	 * Update temporary item price
	 *
	 * @param {CartState} state
	 * @param {string} price
	 * @return {void}
	 */
	UPDATE_TEMPORARY_ITEM_PRICE(state: CartState, prices: Prices): void {
		Vue.set(state.temporarySelectedItem, 'price', prices.price);

		// We wanna update the adjusted price of the item if there is already one from member / subsidy dicount.
		// If the pricing option had no adjustedPrice, we still update the adjustedPrice with
		// the default price of the pricing option, to keep everything in sync.
		if(state.temporarySelectedItem.adjustedPrice || prices.adjustedPrice) {
			if(prices.adjustedPrice) {
				Vue.set(state.temporarySelectedItem, 'adjustedPrice', prices.adjustedPrice);
			}
			else {
				Vue.set(state.temporarySelectedItem, 'adjustedPrice', prices.price);
			}
		}
	},

	/**
	 * Remove unique OrderItem from list
	 *
	 * @param {CartState} state
	 * @param {OrderItem} menuItem
	 * @return {void}
	 */
	REMOVE_FULL_ITEM_FROM_CART(state: CartState, payload: { menuItem: OrderItem, orderDate?: string }): void {
		const { menuItem, orderDate } = payload;

		// Order date will only be present if ordering from a rotational menu
		if (orderDate) {
			const order = state.rotationalMenuOrders.find((order) => order.date === orderDate);

			if (order) {
				const index = order.items.findIndex(item => item.orderId === menuItem.orderId);

				if(index > -1) {
					order.items.splice(index, 1);

					if (!order.items.length) {
						state.rotationalMenuOrders = state.rotationalMenuOrders.filter((order) => order.date !== orderDate);
					}
				}

				updateRotationalMenuOrdersCookie({ orderId: order.orderId, date: orderDate, items: order?.items || [] }, restaurantState.restaurant.id);
			}
		}
		else {
			const index = state.items.findIndex(item => item.orderId === menuItem.orderId);

			if(index > -1) {
				state.items.splice(index, 1);
				updateCartCookie(state.items, restaurantState.restaurant.id, suitesState.eventSuiteId);
			}
		}
	},

	/**
	 * Set items and costs calculated from the database
	 *
	 * @param {CartState} state
	 * @param {OrderItem} tempItem
	 * @return {void}
	 */
	SET_COSTS(state: CartState, payload: any): void {
		Vue.set(state, 'items', payload.orderItems);
		Vue.set(state, 'costs', payload.costs);
		Vue.set(state, 'queue', payload.queue);
		Vue.set(state, 'warning', payload.warning);

		// Suites and catering orders require a payment method
		const orderTypeRequiresPaymentMethod = suitesState.preOrdering || suitesState.eventDayOrdering || state.config.genericCatering === 'true';
		const paymentNotRequired = restaurantState.restaurant.payment_processor === 'payOnPremise' || payload.costs.totalWithTip === 0;
		Vue.set(state.checkout, 'paymentNotRequired', !orderTypeRequiresPaymentMethod && paymentNotRequired);
	},

	/**
	 * Set next order queue time
	 *
	 * @param {CartState} state
	 * @param {QueueInfo} queueInfo
	 * @return {void}
	 */
	SET_QUEUE(state: CartState, queueInfo: QueueInfo): void {
		Vue.set(state, 'queue', queueInfo);
	},

	/**
	 * Set tax rate of restaurant
	 * TODO: SET ALL RESTAURANT INFO UNDER A RESTAURANT OBJECT IN STATES
	 *
	 * @param {CartState} state
	 * @param {Restaurant} restaurant
	 * @return {void}
	 */
	SET_RESTAURANT_CHARGES(state: CartState, restaurant: Restaurant): void {
		if(restaurant.serviceCharge) {
			// Get the value without the symbol ($, %)
			Vue.set(state.costs.serviceCharge, 'type', restaurant.serviceCharge.slice(restaurant.serviceCharge.length - 1) === '$' ? 'dollar' : 'percentage');

			// Set the percentage value for display purposes
			if(state.costs.serviceCharge.type === 'percentage') {
				Vue.set(state.costs.serviceCharge, 'percentageValue', +restaurant.serviceCharge.slice(0, -1));
			}
			Vue.set(state.costs.serviceCharge, 'label', restaurant.serviceChargeLabel);
		}
		if(restaurant.deliveryCharge) {
			// Get the value without the symbol ($, %)
			Vue.set(state.costs.deliveryCharge, 'type', restaurant.deliveryCharge.slice(restaurant.deliveryCharge.length - 1) === '$' ? 'dollar' : 'percentage');

			// Set the percentage value for display purposes
			if(state.costs.deliveryCharge.type === 'percentage') {
				Vue.set(state.costs.deliveryCharge, 'percentageValue', +restaurant.deliveryCharge.slice(0, -1));
			}
		}
	},

	/**
	 * Set the value from the browsers cookies to our store.
	 * This includes contact info, membership flag, and legal check.
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	GET_COOKIES(state: CartState): void {
		const name = Cookies.get('name');
		const email = Cookies.get('email');
		const phone = Cookies.get('phone');

		if(name || email || phone) {
			Vue.set(state.checkout, 'contact', {
				full_name: name ? name : '',
				email: email ? email : '',
				phone_number: phone ? phone : ''
			});
		}

		const legal = Cookies.get('legal') === 'true';
		if(legal) {
			Vue.set(state, 'legal', legal);
		}
	},

	/**
	 * Set cart items from the cookies
	 *
	 * @param {CartState} state
	 * @param {boolean} orderingRequiresLogin
	 * @return {void}
	 */
	SET_CART_FROM_COOKIES(state: CartState, orderingRequiresLogin: boolean): void {
		const cart = Cookies.get(CART_COOKIE) ? JSON.parse(Cookies.get(CART_COOKIE)!) : null;
		const rotationalMenuOrders = Cookies.get(ROTATIONAL_MENU_ORDERS_COOKIE) ? JSON.parse(Cookies.get(ROTATIONAL_MENU_ORDERS_COOKIE)!) : null;

		// Check if the cart/rotationalMenuOrders cookie is from the same location as the current restaurant
		const atSameLocation = (cart?.id || rotationalMenuOrders?.id) === restaurantState.restaurant.id;

		// If the user is not required to login to order, we can load the cart
		// If the user is logged in, we can always load the cart (assuming other conditions pass)
		const authenticatedOrLoginNotRequired = (!orderingRequiresLogin || authState.authenticated);

		// Suites edge cases
		// If the user is pre-ordering, we only want to load the cart if the user has not already pre-ordered (already handled by pre-order confirmation modal)
		// If pre-order is not placed or event day ordering, only load the cart if the selected event suite id matches the cart cookie's event suite id
		// If the user is not placing a suites order, remove the cookie if there is an event suite id in the cart cookie
		const alreadyPreOrdered = !!(suitesState.preOrdering && suitesState.userEventSuite?.orders.length);
		const validSuitesCart = (!alreadyPreOrdered || suitesState.eventDayOrdering) && suitesState.eventSuiteId === cart?.eventSuiteId;
		const notSuitesOrder = !!(!(suitesState.preOrdering || suitesState.eventDayOrdering) && !cart?.eventSuiteId);

		if (atSameLocation && authenticatedOrLoginNotRequired && (validSuitesCart || notSuitesOrder)) {
			if (cart?.items && !restaurantState.restaurant.k12) {
				Vue.set(state, 'items', getItemsFromCookie(cart.items, restaurantState, state));
				reapplySubsidyPricingToCartItems(state.items);
			}

			if (rotationalMenuOrders?.orders && restaurantState.restaurant.k12) {
				Vue.set(state, 'rotationalMenuOrders', getRotationalMenuOrdersFromCookie(rotationalMenuOrders.orders, restaurantState, state));
				state.rotationalMenuOrders.forEach(order => reapplySubsidyPricingToCartItems(order.items));
			}
		}
		else {
			state.items = [];
			Cookies.remove(CART_COOKIE);
			Cookies.remove(ROTATIONAL_MENU_ORDERS_COOKIE);
		}
	},

	/**
	 * Set the cookies in the browser with the contact information
	 * filled out by the user
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	SET_COOKIES(state: CartState): void {
		if(state.checkout.contact) {
			Cookies.set('name', state.checkout.contact.full_name);
			Cookies.set('email', state.checkout.contact.email);
			Cookies.set('phone', state.checkout.contact.phone_number);
		}

		if(state.checkout.memberInfo && state.checkout.memberInfo.id) {
			Cookies.set(`${restaurantState.restaurant.slug}-member`, state.checkout.memberInfo.identifier);
		}
	},

	/**
	 * Set the loyalty program payload
	 *
	 * @param {CartState} state
	 * @param {CheckoutLoyaltyProgramInfo} payload
	 * @return {void}
	 */
	SET_LOYALTY_PROGRAM(state: CartState, payload: CheckoutLoyaltyProgramInfo): void {
		Vue.set(state.checkout, 'loyaltyProgram', payload);
	},

	/**
	 * Set loyalty program user status
	 *
	 * @param {CartState} state
	 * @param {string} value
	 * @return {void}
	 */
	SET_USER_LOYALTY_PROGRAM_STATUS(state: CartState, value: string): void {
		if(state.checkout && state.checkout.loyaltyProgram) {
			Vue.set(state.checkout.loyaltyProgram, 'userStatus', value);
		}
	},

	/**
	 * Update the order options when selecting an addon
	 *
	 * @param {CartState} state
	 * @param {any} options
	 * @return {void}
	 */
	UPDATE_ORDER_OPTIONS(state: CartState, options: any): void {
		if(state.temporarySelectedItem) {
			// Here we need to compromise with vue 2 reactivity issues. Since Vue
			// does not want to run comparisons across the whole keys and values for
			// performance reason, we have to change the object completely to allow
			// Vue to determine that the object has changed (nested objects problem)
			// (VUE 3 FIXES THIS)
			Vue.set(state.temporarySelectedItem, 'orderOptions', null);
			Vue.set(state.temporarySelectedItem, 'orderOptions', options);
			state.temporarySelectedItem = { ...setLocalOrderPrice(state.temporarySelectedItem) };
		}
	},

	/**
	 * Update tip of the complete order
	 *
	 * @param {CartState} state
	 * @param {any} quantityObject
	 * @return {void}
	 */
	UPDATE_TIP(state: CartState, tipObject: any): void {
		if(tipObject) {
			// Ensure positive values (We do this in multiple places, including the backend obviously, better be safe than sorry)
			if(tipObject.tip < 0) {
				tipObject.tip = Math.abs(tipObject.tip);
			}

			if(tipObject.type === 'percentage') {
				Vue.set(state.costs.tip, 'percentageValue', tipObject.tip);
				Vue.set(state.costs.tip, 'amount', 0);
			}
			else {
				Vue.set(state.costs.tip, 'percentageValue', 0);
				Vue.set(state.costs.tip, 'amount', tipObject.tip);
			}
			Vue.set(state.costs.tip, 'type', tipObject.type);
		}
		else {
			Vue.set(state.costs.tip, 'percentageValue', 0);
			Vue.set(state.costs.tip, 'amount', 0);
		}
	},

	/**
	 * Update quantity from a unique OrderItem, we make sure to update the costs
	 * respectively as well.
	 *
	 * @param {CartState} state
	 * @param {any} quantityObject
	 * @return {void}
	 */
	UPDATE_QUANTITY(state: CartState, quantityObject: any): void {
		// Order date will only be present if ordering from a rotational menu, it's used to find the correct order to update
		if (quantityObject.orderDate) {
			const order = state.rotationalMenuOrders.find((order) => order.date === quantityObject.orderDate);

			if (order) {
				const index = order.items.findIndex(item => item.orderId === quantityObject.id);

				if(index > -1) {
					Vue.set(order.items[index], 'quantity', +quantityObject.quantity);
					Vue.set(order.items, index, { ...setLocalOrderPrice(order.items[index]) });
				}
				updateRotationalMenuOrdersCookie({ orderId: order.orderId, date: quantityObject.orderDate, items: order.items }, restaurantState.restaurant.id);
			}
		}
		else {
			const index = state.items.findIndex(item => item.orderId === quantityObject.id);

			if(index > -1) {
				state.items[index].quantity = +quantityObject.quantity;
				state.items[index] = { ...setLocalOrderPrice(state.items[index]) };
				updateCartCookie(state.items, restaurantState.restaurant.id, suitesState.eventSuiteId);
			}
		}
	},

	/**
	 * Update the checkout pickup information
	 *
	 * @param {CartState} state
	 * @param {CheckoutPickupInfo} pickup
	 * @return {void}
	 */
	UPDATE_PICKUP_INFORMATION(state: CartState, pickup: CheckoutPickupInfo): void {
		Vue.set(state.checkout, 'pickup', pickup);
	},

	/**
	 * Update the checkout suites information
	 *
	 * @param {CartState} state
	 * @param {CheckoutSuitesInfo} suitesInfo
	 * @return {void}
	 */
	UPDATE_SUITES_INFORMATION(state: CartState, suitesInfo: CheckoutSuitesInfo): void {
		Vue.set(state.checkout, 'suitesInfo', suitesInfo);
	},

	/**
	 * Update the checkout delivery information
	 *
	 * @param {CartState} state
	 * @param {CheckoutDeliveryInfo} delivery
	 * @return {void}
	 */
	UPDATE_DELIVERY_INFORMATION(state: CartState, delivery: CheckoutDeliveryInfo): void {
		Vue.set(state.checkout, 'delivery', delivery);
	},

	/**
	 * Update the checkout contact information
	 *
	 * @param {CartState} state
	 * @param {CheckoutCardInfo} contact
	 * @return {void}
	 */
	UPDATE_CONTACT_INFORMATION(state: CartState, contact: CheckoutContactInfo): void {
		Vue.set(state.checkout, 'contact', contact);
	},

	/**
	 * Update the anonymous user's card info
	 *
	 * @param {CartState} state
	 * @param {CheckoutCardInfo} card
	 * @return {void}
	 */
	UPDATE_CARD_INFORMATION(state: CartState, card: CheckoutCardInfo): void {
		Vue.set(state.checkout, 'card', card);
	},

	/**
	 * Update the invoice value
	 *
	 * @param {CartState} state
	 * @param {boolean} invoice
	 * @return {void}
	 */
	UPDATE_INVOICE(state: CartState, invoice: boolean): void {
		Vue.set(state.checkout, 'invoice', invoice);
	},

	/**
	 * Update the cost center value
	 *
	 * @param {CartState} state
	 * @param {string} costCenter
	 * @return {void}
	 */
	UPDATE_COST_CENTER(state: CartState, costCenter: string): void {
		Vue.set(state.checkout, 'costCenter', costCenter);
	},

	/**
	 * Update the purchase order value
	 *
	 * @param {CartState} state
	 * @param {string} purchaseOrder
	 * @return {void}
	 */
	UPDATE_PURCHASE_ORDER(state: CartState, purchaseOrder: string): void {
		Vue.set(state.checkout, 'purchaseOrder', purchaseOrder);
	},

	/**
	 * Update the user's answers to the custom questions
	 *
	 * @param {CartState} state
	 * @param {CheckoutCustomQuestion[]} questions
	 * @return {void}
	 */
	UPDATE_CUSTOM_QUESTIONS(state: CartState, questions: CheckoutCustomQuestion[]): void {
		Vue.set(state.checkout, 'questions', questions);
	},

	/**
	 * Update the option to save a payment method for Web Login users
	 *
	 * @param {CartState} state
	 * @param {savePayment} boolean
	 * @return {void}
	 */
	SET_IS_USER_SAVING_PAYMENT_METHOD_ON_CHECKOUT(state: CartState, savePaymentMethodOnCheckout: boolean): void {
		Vue.set(state.checkout, 'savePaymentMethodOnCheckout', savePaymentMethodOnCheckout);
	},

	/**
	 * Update the if the membership banner should be shown
	 *
	 * @param {CartState} state
	 * @param {showBanner} boolean
	 * @return {void}
	 */
	SHOW_MEMBERSHIP_BANNER(state: CartState, showBanner: boolean): void {
		Vue.set(state, 'showMembershipBanner', showBanner);
	},

	/**
	 * Update payment method
	 *
	 * @param {CartState} state
	 * @param {string} paymentMethod
	 * @return {void}
	 */
	UPDATE_PAYMENT_METHOD(state: CartState, paymentMethod: string): void {
		// If the credit card option was chosen and the user is logged in, select a default
		// card depending on his account info
		if(paymentMethod === 'credit_card' && authState.user.id) {
			if(authState.user.paymentMethods && authState.user.paymentMethods.length) {
				authState.user.paymentMethods.forEach(creditCard => {
					if(creditCard.isDefault) {
						state.chosenCreditCard = creditCard;
					}
				});
				// If somehow there is no default card, select the first one in the array
				if(!state.chosenCreditCard!.paymentCardId.length) {
					state.chosenCreditCard = authState.user.paymentMethods[1];
				}
			}
			else {
				state.chosenCreditCard = null;
			}
		}
		// Make sure to clear out any CC details if the payment method is
		// switched from CC to another one.
		else {
			state.checkout.card = {
				number: '',
				cvd: '',
				expiry_date: '',
				postal_code: '',
				type: ''
			};
			state.chosenCreditCard = null;
			state.checkout.savePaymentMethodOnCheckout = false;
		}

		// Extra payment methods
		state.checkout.invoice = (paymentMethod === 'invoice');

		if(paymentMethod !== 'costCenter') {
			state.checkout.costCenter = null;
		}

		if(paymentMethod !== 'purchaseOrder') {
			state.checkout.purchaseOrder = null;
		}

		Vue.set(state.checkout, 'paymentMethod', paymentMethod);
	},

	/**
	 * Update the anonymous user's chosen credit card
	 *
	 * @param {CartState} state
	 * @param {SavedPaymentOption} chosenCreditCard
	 * @return {void}
	 */
	UPDATE_CHOSEN_CREDIT_CARD(state: CartState, chosenCreditCard: SavedPaymentOption | null): void {
		Vue.set(state, 'chosenCreditCard', chosenCreditCard);
	},

	/**
	 * Update the user's App8 saved payment credit cards
	 * If there is no chosenCreditCard we cycle through and
	 * check for the default card on the user profile, if for
	 * some reason the user has cards but no default one, we
	 * put the chosenCreditCard to the first row
	 *
	 *
	 * @param {CartState} state
	 * @param {SavedPaymentOption[]} methods
	 * @return {void}
	 */
	UPDATE_PAYMENT_OPTION(state: CartState, methods: SavedPaymentOption[]): void {
		if(methods) {
			if(methods.length > 1) {
				methods.sort((a: SavedPaymentOption, b: SavedPaymentOption) => Number(b.isDefault) - Number(a.isDefault));
				methods.forEach(method => {
					if(method.isDefault) {
						state.chosenCreditCard = method;
					}
				});
			}
			else if (methods.length === 1) {
				state.chosenCreditCard = methods[0];
			}
		}
	},

	/**
	 * Save query params to the vuex store
	 *
	 * @param {CartState} state
	 * @param {any} queryParams
	 * @return {void}
	 */
	PROCESS_QUERY_PARAMS(state: CartState, queryParams: any): void {
		// Menu config
		state.config.tableNum = queryParams['tableNum'];
		state.config.takeout = queryParams['takeout'];
		state.config.takeoutType = queryParams['takeoutType'];
		state.config.genericCatering = queryParams['catering'];
		state.config.viewOnly = queryParams['viewOnly'];
		state.config.menuList = queryParams['menuList'];
		state.config.genericTableLocation = queryParams['genericTableLocation'];
		suitesState.eventSuiteId = +queryParams['eventSuite'];
		suitesState.eventDayOrdering = !!queryParams['eventDayOrdering'];
		if((state.config.takeout || state.config.genericCatering || restaurantState.restaurant.k12) && restaurantState.restaurant.delivery && restaurantState.restaurant.delivery.type && state.config.takeoutType !== 'pickup') {
			Vue.set(state.checkout, 'pickup', { delivery: true });
		}

		// Temporary date query params
		state.config.takeoutDateRange = queryParams['dateRange'];
		state.config.takeoutSpecificDates = queryParams['dates'];

		// Rotational menu date query params
		const startDate = DateTime.fromISO(queryParams['selectedWeek']);
		restaurantState.rotationalMenuDateRange = {
			start: startDate.startOf('week').toISODate(),
			end: startDate.endOf('week').toISODate()
		};
	},

	/**
	 * Set the selected table (number and area)
	 *
	 * @param {CartState} state
	 * @param {GenericTableLocation} payload
	 * @return {void}
	 */
	SET_SELECTED_TABLE(state: CartState, payload: GenericTableLocation): void {
		Vue.set(state.config, 'tableNum', payload.tableNum);
		if(payload.location) {
			Vue.set(state.config, 'tableInfo', { tableNum: payload.tableNum, tableLocation: payload.location, tableArea: payload.area });
		}
	},

	/**
	 * Update the modal error message
	 *
	 * @param {CartState} state
	 * @param {ErrorValidation} errorValidation
	 * @return {void}
	 */
	UPDATE_VALIDATION_ERROR(state: CartState, errorValidation: ErrorValidation): void {
		Vue.set(state, 'errorValidation', errorValidation);
	},

	/**
	 * Update the delivery status *NEEDS REFACTOR*
	 *
	 * @param {CartState} state
	 * @param {any} value
	 * @return {void}
	 */
	UPDATE_DELIVERY_STATUS(state: CartState, value: any): void {
		Vue.set(state.checkout, 'pickup', value);
	},

	/**
	 * Update the today date value to prevent adding required notice, etc to it if
	 * the user go back to the takeoutinformation component
	 *
	 * @param {CartState} state
	 * @param {any} value
	 * @return {void}
	 */
	SET_TODAY_DATE(state: CartState, value: any): void {
		Vue.set(state, 'today', value);
	},

	/**
	 * User selected a date from the datepicker, set the boolean to true.
	 * This flag is used for the date range and dates at the moment, to not set
	 * the date again when the user go back to the component.
	 *
	 * @param {CartState} state
	 * @param {any} value
	 * @return {void}
	 */
	SET_DATE_SELECTED_BY_USER_FLAG(state: CartState, value: any): void {
		Vue.set(state, 'dateSelectedByUser', value);
	},

	/**
	 * Set the tip selected to the boolean value
	 *
	 * @param {CartState} state
	 * @param {boolean} value
	 * @return {void}
	 */
	SET_TIP_SELECTED(state: CartState, value: boolean): void {
		Vue.set(state, 'tipSelected', value);
	},

	/**
	 * Set the google pay payment payload
	 *
	 * @param {CartState} state
	 * @param {CheckoutGooglePayPayload | null} googlePayPaymentPayload
	 * @return {void}
	 */
	SET_GOOGLE_PAY_PAYMENT_PAYLOAD(state: CartState, googlePayPaymentPayload: CheckoutGooglePayPayload | null): void {
		Vue.set(state.checkout, 'googlePayPaymentPayload', googlePayPaymentPayload);
	},

	/**
	 * Set the apple pay payment token
	 *
	 * @param {CartState} state
	 * @param {string | null} applePayPaymentToken
	 * @return {void}
	 */
	SET_APPLE_PAY_PAYMENT_PAYLOAD(state: CartState, applePayPaymentPayload: ApplePayPaymentPayload): void {
		Vue.set(state.checkout, 'applePayPaymentToken', applePayPaymentPayload.applePayPaymentToken);
		Vue.set(state.checkout, 'applePayPaymentDetails', applePayPaymentPayload.applePayDetails);
	},

	/**
	 * Set the legal check to true
	 *
	 * @param {CartState} state
	 * @param {boolean} value
	 * @return {void}
	 */
	SET_LEGAL(state: CartState, value: boolean): void {
		Vue.set(state, 'legal', value);
	},

	/**
	 * Set the discount applied
	 *
	 * @param {CartState} state
	 * @param {any} discount
	 * @return {void}
	 */
	SET_DISCOUNT(state: CartState, discount: any): void {
		// Set the total to 0 first before the calculations for reset
		Vue.set(state.costs.discount, 'amount', 0);
		if(discount && discount.name) {
			Vue.set(state.costs.discount, 'code', discount.name);
			Vue.set(state.costs.discount, 'type', discount.value.slice(discount.value.length - 1) === '$' ? 'dollar' : 'percentage');

			// Set the percentage value for display purposes
			if(state.costs.discount.type === 'percentage') {
				Vue.set(state.costs.discount, 'percentageValue', +discount.value.slice(0, -1));
			}
		}
		else {
			Vue.set(state.costs.discount, 'code', '');
			Vue.set(state.costs.discount, 'percentageValue', 0);
			Vue.set(state.costs.discount, 'type', 'percentage');
		}
	},

	/**
	 * Set the voucher applied
	 *
	 * @param {CartState} state
	 * @param {any} voucher
	 * @return {void}
	 */
	SET_VOUCHER(state: CartState, voucher: any): void {
		if(voucher && voucher.code) {
			Vue.set(state.costs.voucher, 'code', voucher.code);
			Vue.set(state.costs.voucher, 'balance', voucher.balance);
		}
		else {
			Vue.set(state.costs.voucher, 'code', '');
			Vue.set(state.costs.voucher, 'balance', 0);
		}
		Vue.set(state.costs.voucher, 'amount', 0);
	},

	/**
	 * Set the credits applied
	 *
	 * @param {CartState} state
	 * @param {{ id: string; balance: number; amount: number; }} credit
	 * @return {void}
	 */
	SET_CREDITS(state: CartState, credit: { id: string; balance: number; amount: number; }): void {
		Vue.set(state.costs, 'credits', credit);
	},

	/**
	 * Set the subsidy discount amount
	 *
	 * @param {CartState} state
	 * @param {number} amount
	 * @return {void}
	 */
	SET_CHOSEN_SUBSIDY_DISCOUNT(state: CartState, chosenAmountToBePaid: number): void {
		Vue.set(state.costs.subsidyDiscount, 'chosenAmountToBePaid', chosenAmountToBePaid);
	},

	/**
	 * Calculate the cart item prices (member, subsidy, regular) when
	 * pricing changes are applied
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	CALCULATE_CART_ITEMS_ORDER_PRICES(state: CartState): void {
		if(state.items && state.items.length) {
			state.items.forEach((item: OrderItem, index: number) => {
				state.items[index] = { ...setLocalOrderPrice(item) };
			});
		}
	},

	/**
	 * Remove all rotational menu orders that have passed the cutoff date
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	REMOVE_ORDERS_PASSED_CUTOFF(state: CartState): void {
		const cutoffDate = restaurantState.restaurant.orderCutoffDate;
		if (cutoffDate) {
			state.rotationalMenuOrders = state.rotationalMenuOrders.filter(order => {
				const orderDate = DateTime.fromISO(order.date).startOf('day');
				return orderDate >= cutoffDate;
			});
			// Update rotational menu orders cookie to remove orders past cutoff
			Cookies.remove(ROTATIONAL_MENU_ORDERS_COOKIE);
			if (state.rotationalMenuOrders.length) {
				state.rotationalMenuOrders.forEach((order: RotationalMenuOrder) => updateRotationalMenuOrdersCookie(order, restaurantState.restaurant.id));
			}
		}
	},

	/**
	 * Clear only cart items and rotational menu orders
	 * If we edit this mutation, please make sure to edit CLEAR_ITEMS_AND_CARD_INFO as well
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	CLEAR_CART_ITEMS(state: CartState): void {
		state.items = [];
		state.rotationalMenuOrders = [];
		// Subsidy discount reset, force reactivity on subtotal as well for slider
		Vue.set(state.costs, 'subtotal', 0);
		Object.assign(state.costs.subsidyDiscount, {
			type: '',
			chosenAmountToBePaid: undefined,
			code: undefined,
			amount: 0,
			baseSubsidizedAmount: 0
		});
		Cookies.remove(CART_COOKIE);
		Cookies.remove(ROTATIONAL_MENU_ORDERS_COOKIE);
	},

	/**
	 * Clear the store's items and card info
	 * If we edit this mutation, please make sure to edit CLEAR_CART_ITEMS as well
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	CLEAR_ITEMS_AND_CARD_INFO(state: CartState): void {
		state.items = [];
		state.rotationalMenuOrders = [];
		state.checkout.card = {
			number: '',
			cvd: '',
			expiry_date: '',
			postal_code: '',
			type: ''
		};
		state.costs.voucher = {
			amount: 0,
			balance: 0,
			code: ''
		};
		state.costs.credits = {
			id: '',
			balance: 0,
			amount: 0
		};
		// Only reset the delivery info for k12 ordering after an the order is submitted if the user is authenticated and has more than 1 student.
		if(restaurantState.restaurant.k12 && authState.authenticated && (authState.user?.students?.length ?? 0) > 1) {
			state.checkout.delivery = {
				type: state.checkout.delivery?.type ?? '',
				name: '',
				student: '',
				school: '',
				grade: '',
				classroom: '',
				teacher: ''
			};
		}
		state.checkout.googlePayPaymentPayload = null;
		state.checkout.applePayPaymentToken = null;
		state.checkout.invoice = false;
		state.checkout.costCenter = null;
		state.checkout.purchaseOrder = null;
		state.checkout.loyaltyProgram = null;
		state.checkout.savePaymentMethodOnCheckout = false;
		// Subsidy discount reset, force reactivity on subtotal as well for slider
		Vue.set(state.costs, 'subtotal', 0);
		Object.assign(state.costs.subsidyDiscount, {
			type: '',
			chosenAmountToBePaid: undefined,
			code: undefined,
			amount: 0,
			baseSubsidizedAmount: 0
		});
		// Filter out checkbox questions
		if (state.checkout.questions?.length && restaurantState.restaurant.customQuestions?.length) {
			const filteredQuestions = state.checkout.questions.filter(checkoutQuestion => {
				const foundQuestion = restaurantState.restaurant.customQuestions!.find(restaurantQuestion => restaurantQuestion.question === checkoutQuestion.question);
				return !(foundQuestion && foundQuestion.question_type === 'checkbox');
			});
			Vue.set(state.checkout, 'questions', filteredQuestions);
		}

		Vue.set(state, 'temporarySelectedItem', {});
		Cookies.remove(CART_COOKIE);
		Cookies.remove(ROTATIONAL_MENU_ORDERS_COOKIE);
	},

	/**
	 * Reset the store's checkout info and items to their initial state.
	 * If the user information is set already, we fill it up.
	 * We do not reset member info.
	 *
	 * @param {CartState} state
	 * @return {void}
	 */
	RESET_CHECKOUT_PAYMENT_INFO_AND_ITEMS(state: CartState): void {
		// Only reset the items if ordering requires login and the user is not authenticated
		if (store.getters['restaurant/orderingRequiresLogin'] && !authState.authenticated) {
			state.items = [];
			state.rotationalMenuOrders = [];
			Cookies.remove(CART_COOKIE);
			Cookies.remove(ROTATIONAL_MENU_ORDERS_COOKIE);
		}
		state.chosenCreditCard = null;
		Vue.set(state, 'checkout', {
			paymentMethod: '',
			pickup: null,
			delivery: null,
			contact: authState.user && authState.user.id ?
				{
					full_name: authState.user.firstName + ' ' + authState.user.lastName,
					phone_number: authState.user.phoneNumber,
					email: authState.user.email
				} :
				null,
			card: null,
			savePaymentMethodOnCheckout: false,
			applePayPaymentToken: null,
			googlePayPaymentPayload: null,
			invoice: false,
			costCenter: null,
			purchaseOrder: null,
			memberInfo: state.checkout && state.checkout.memberInfo ? state.checkout.memberInfo : null,
			loyaltyProgram: null,
			suitesInfo: null
		});
		if ((state.config.takeout || state.config.genericCatering || restaurantState.restaurant.k12) && restaurantState.restaurant.delivery?.type && state.config.takeoutType !== 'pickup') {
			Vue.set(state.checkout, 'pickup', { delivery: true });
		}
	}
};